import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  buyerSubmissionsApi,
  type BuyerReferralDisclosureState,
  type BuyerSubmissionPackageDto,
  type ReferralBroker,
  type SetReferralDisclosurePayload,
} from "@/api/buyers";
import { Button, Input, Label, useToast } from "@/design-system/components/ui";
import { ConfirmModal } from "@/components/ui/PromptModals";
import {
  CounterpartyPicker,
  type SelectedCounterparty,
} from "@/components/portfolioAdmin/loanDetail/parties/CounterpartyPicker";
import { buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { brokerPickerLabel } from "@/lib/brokerDisplay";
import {
  DEFAULT_REFERRAL_FEE_PERCENT,
  REFERRAL_DISCLOSURE_HEADING,
  isPrintable,
  referralDisclosureBody,
  referralFeeProblem,
  referralNamesFor,
} from "@/lib/referralDisclosure";
import { useDateFormatter } from "@/utils/datetime";

export const REFERRAL_LABEL = "Referring broker disclosure";

export const REFERRAL_HELP =
  "When this is on, the cover letter ends with a Referral Disclosure stating that the referring "
  + "broker introduced this buyer and requests a co-broker fee, subject to agreement with the "
  + "listing broker. Phoenix receives no portion of the fee.";

export const REFERRAL_SUGGESTED_CAPTION = "Suggested from the buyer's prequalification site.";
export const REFERRAL_NOT_PRINTABLE =
  "This broker record has no contact name or firm name to print.";
export const REFERRAL_LISTING_BROKER_WARNING = "This broker is also the listing broker on this listing.";

/** On, but the saved broker's record no longer has a printable name, so the
 *  letter renders without the disclosure. Said loudly, to every viewer. */
export const REFERRAL_NOT_PRINTING =
  "This is on, but the referring broker's record has no printable name, so the cover letter "
  + "does not carry the disclosure. Fix the broker record or choose another broker.";

export function referralTurnOffMessage(alreadySent: boolean): string {
  return "Turn off the Referral Disclosure for this submission? The cover letter will no longer carry it."
    + (alreadySent ? " The package has already been sent; this applies to future sends only." : "");
}

/** The name fields the preview reads — both a `ReferralBroker` (server state)
 *  and a picked `BrokerSummaryDto` carry them. */
interface BrokerNameFields {
  name?: string | null;
  companyName?: string | null;
  dba?: string | null;
}

/** A broker from the server state as the picker's controlled chip. */
function chipFor(b: ReferralBroker): SelectedCounterparty {
  const detail = [(b.name ?? "").trim(), b.status].filter(Boolean).join(" · ");
  return {
    id: b.id,
    name: brokerPickerLabel(b).primary,
    detail: b.isArchived ? `${detail} (archived)` : detail,
    data: b,
  };
}

interface Draft {
  broker: SelectedCounterparty | null;
  feeText: string;
  /** The broker came from the D9 suggestion (never saved by itself). */
  suggested: boolean;
}

/** Pre-fill order: the effective setting, then the buyer default, then the D9
 *  suggestion (picker only); the fee falls back to the program default. */
function initialDraft(state: BuyerReferralDisclosureState): Draft {
  const broker = state.broker ?? state.buyerDefaultBroker ?? state.suggestedBroker;
  const fee = state.feePercent ?? state.buyerDefaultFeePercent ?? DEFAULT_REFERRAL_FEE_PERCENT;
  return {
    broker: broker ? chipFor(broker) : null,
    feeText: String(fee),
    suggested: broker !== null && !state.broker && !state.buyerDefaultBroker,
  };
}

/** Strict decimal parse: anything that is not a plain number is "missing". */
function parseFee(text: string): number | null {
  const t = text.trim();
  return /^-?(\d+\.?\d*|\.\d+)$/.test(t) ? Number(t) : null;
}

/**
 * The cover letter's Referral Disclosure for one submission (D5/D6/D8).
 *
 * <p>Checking an unchecked box opens the inline editor and saves NOTHING until
 * SAVE (ruling Q7); the D9 suggestion only pre-fills the picker. Unchecking a
 * saved disclosure asks first and saves "off". Inheritance, the sent line and
 * the printed body are the server's; the live preview uses the shared wording
 * mirror (`lib/referralDisclosure.ts`).</p>
 */
export function ReferralDisclosureControl({
  submissionId,
  pkg,
  canEdit,
}: {
  submissionId: string;
  pkg: BuyerSubmissionPackageDto;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const { date } = useDateFormatter();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirmingOff, setConfirmingOff] = useState(false);

  const reset = () => {
    setEditing(false);
    setDraft(null);
  };

  const save = useMutation({
    mutationFn: (body: SetReferralDisclosurePayload) =>
      buyerSubmissionsApi.setReferralDisclosure(submissionId, body),
    onSuccess: (next, body) => {
      // The PUT answers with the package, so the box does not flicker back
      // while the prefix refetch (list status, send drafts) is in flight.
      qc.setQueryData(buyerSubmissionKeys.package(submissionId), next);
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      reset();
      toast.success(body.enabled ? "Referral disclosure saved." : "Referral disclosure turned off.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toDefault = useMutation({
    mutationFn: () => buyerSubmissionsApi.useBuyerDefaultReferralDisclosure(submissionId),
    onSuccess: (next) => {
      qc.setQueryData(buyerSubmissionKeys.package(submissionId), next);
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      reset();
      toast.success("Using the buyer default.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const state = pkg.referralDisclosure;
  if (!state) return null;

  const serverOn = state.enabled;
  const checked = serverOn || editing;
  const busy = save.isPending || toDefault.isPending;
  const current = draft ?? initialDraft(state);

  const fee = parseFee(current.feeText);
  const feeProblem = referralFeeProblem(fee);
  const names = current.broker
    ? referralNamesFor(
        (current.broker.data as BrokerNameFields | undefined)?.name,
        (current.broker.data as BrokerNameFields | undefined)?.companyName,
        (current.broker.data as BrokerNameFields | undefined)?.dba,
      )
    : null;
  const notPrintable = names !== null && !isPrintable(names);
  const preview =
    names && fee !== null && !feeProblem
      ? referralDisclosureBody(state.buyerNameAsPrinted, names, fee)
      : null;

  const unchanged =
    serverOn && current.broker?.id === state.broker?.id && fee === state.feePercent;
  const canSave = !busy && current.broker !== null && !feeProblem && !notPrintable && !unchanged;

  const shownBrokerId = canEdit ? current.broker?.id : state.broker?.id;
  const alsoListingBroker =
    state.listingBrokerId !== null && checked && shownBrokerId === state.listingBrokerId;

  const onToggle = () => {
    if (serverOn) setConfirmingOff(true);
    else if (editing) reset();
    else setEditing(true);
  };

  const update = (patch: Partial<Draft>) => setDraft({ ...current, ...patch });

  return (
    <div className="grid gap-space-1" data-testid="referral-disclosure">
      <label className="flex items-center gap-space-2 text-small font-medium">
        <input
          type="checkbox"
          checked={checked}
          disabled={!canEdit || busy}
          onChange={onToggle}
        />
        {REFERRAL_LABEL}
      </label>
      <p className="text-caption text-gray-mid m-0">{REFERRAL_HELP}</p>

      {state.source === "BuyerDefault" && serverOn ? (
        <p className="text-caption text-gray-mid m-0" data-testid="referral-inheritance">
          From buyer default
        </p>
      ) : null}
      {state.source === "Submission" ? (
        <p className="text-caption text-gray-mid m-0" data-testid="referral-inheritance">
          Set for this submission
          {canEdit ? (
            <>
              {" · "}
              <button
                type="button"
                className="text-gold-shadow hover:underline"
                disabled={busy}
                onClick={() => toDefault.mutate()}
              >
                Use buyer default
              </button>
            </>
          ) : null}
        </p>
      ) : null}

      {serverOn && !state.disclosureText ? (
        <p role="alert" className="text-caption text-status-error m-0" data-testid="referral-not-printing">
          {REFERRAL_NOT_PRINTING}
        </p>
      ) : null}

      {state.lastSentAtUtc ? (
        <p className="text-caption text-gray-mid m-0" data-testid="referral-sent-line">
          The package sent {date(state.lastSentAtUtc)}{" "}
          {state.lastSentIncluded ? "included" : "did not include"} this disclosure. Changes apply
          to future sends only.
        </p>
      ) : null}

      {checked && canEdit ? (
        <div
          className="grid gap-space-2 rounded-md border border-border-light p-space-3"
          data-testid="referral-panel"
        >
          <div className="grid gap-space-1">
            <CounterpartyPicker
              counterpartyType="ReferringBroker"
              noun="broker"
              label="Referring broker"
              value={current.broker}
              onChange={(broker) => update({ broker, suggested: false })}
            />
            {current.suggested && current.broker ? (
              <p className="text-caption text-gray-mid m-0" data-testid="referral-suggested">
                {REFERRAL_SUGGESTED_CAPTION}
              </p>
            ) : null}
          </div>

          <div className="grid gap-space-1">
            <Label htmlFor="referral-fee">Co-broker fee %</Label>
            <Input
              id="referral-fee"
              type="text"
              inputMode="decimal"
              value={current.feeText}
              invalid={feeProblem !== null}
              onChange={(e) => update({ feeText: e.target.value })}
              data-testid="referral-fee"
            />
            {feeProblem ? (
              <p className="text-caption text-status-error m-0" data-testid="referral-fee-problem">
                {feeProblem}
              </p>
            ) : null}
          </div>

          {alsoListingBroker ? <ListingBrokerWarning /> : null}

          {notPrintable ? (
            <p className="text-caption text-status-error m-0" data-testid="referral-not-printable">
              {REFERRAL_NOT_PRINTABLE}
            </p>
          ) : null}
          {preview ? <Preview text={preview} /> : null}

          <div className="flex flex-wrap gap-space-2">
            <Button
              size="sm"
              disabled={!canSave}
              loading={save.isPending}
              onClick={() =>
                save.mutate({ enabled: true, brokerId: current.broker?.id ?? null, feePercent: fee })
              }
            >
              SAVE
            </Button>
            {!serverOn ? (
              <Button size="sm" variant="secondary" disabled={busy} onClick={reset}>
                CANCEL
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}

      {!canEdit && serverOn ? (
        <>
          {alsoListingBroker ? <ListingBrokerWarning /> : null}
          {state.disclosureText ? <Preview text={state.disclosureText} /> : null}
        </>
      ) : null}

      <ConfirmModal
        open={confirmingOff}
        title={`Turn off “${REFERRAL_LABEL}”?`}
        message={referralTurnOffMessage(Boolean(pkg.sentAtUtc))}
        confirmLabel="Turn off"
        onConfirm={() => {
          setConfirmingOff(false);
          save.mutate({ enabled: false });
        }}
        onCancel={() => setConfirmingOff(false)}
      />
    </div>
  );
}

function ListingBrokerWarning() {
  return (
    <p className="text-caption text-status-warning m-0" data-testid="referral-listing-broker">
      {REFERRAL_LISTING_BROKER_WARNING}
    </p>
  );
}

function Preview({ text }: { text: string }) {
  return (
    <div className="grid gap-space-1" data-testid="referral-preview">
      <p className="text-small font-semibold m-0">{REFERRAL_DISCLOSURE_HEADING}</p>
      <p className="text-small m-0" data-testid="referral-preview-body">
        {text}
      </p>
    </div>
  );
}
